
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
// import { Form, Input, Button, Row, Col, Select, DatePicker } from "antd";
// import { visitorService } from "@iso/services";
import { visitorService } from "../../../services/visitor.service";
import IntlMessages from "@iso/components/utility/intlMessages";
import VisitorModalWrapper from "../Visitor/VisitorModal.styles";
import { Checkbox, Radio } from "antd";
import { Map, TileLayer, FeatureGroup, Polygon, Tooltip, ZoomControl, Marker, Popup } from 'react-leaflet';
import { Form, Input, Button, Select, Row, Col, DatePicker, Card, Timeline, Upload, message, Tooltip as AntTooltip, Modal } from 'antd';
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import _ from "lodash";

import moment from "moment";
import VisitorSummaryStyles from "./visitorSummary.styles";
import { visitorInductionService } from "@iso/services";
import { INDUCTION_STATE_META, getVisitorType } from "@iso/constants/visitor";
import { getFormPurposeLabel } from "@iso/constants/formPurposes";
import ViewSurveyResultModal from "../Form/ViewSurveyResultModal";



const { Option } = Select;

// How many of a visitor's inductions the drawer lists. The badge on the row
// shows the latest one; this is the recent history behind it.
const INDUCTION_HISTORY_LIMIT = 5;

// moment(undefined) is "now" and moment('-') is "Invalid date" — both were on
// screen before this guard. A missing date should read as a dash.
const fmtDay = (value) => (value ? moment(value).format("ll") : "-");

const VisitorViewSummary = (props) => {
    const { viewSummary, canVerify, currentUserId, onVerified } = props;

    const summaryType = getVisitorType(props.viewSummary || {});

    const [inductions, setInductions] = useState([]);
    const [loadingInductions, setLoadingInductions] = useState(false);
    // The submitted induction being viewed, in the shape ViewSurveyResultModal
    // expects (see FormModal's showSchemaSubmission — same contract).
    const [inductionResult, setInductionResult] = useState(null);
    const [openingSubmission, setOpeningSubmission] = useState(null);
    // Which inductions this person has actually opened in this session. Accept
    // and Refuse stay disabled until they have — verifying is a statement that
    // you read the answers, and a one-click Approve on the row would make that
    // statement for people who never looked.
    const [reviewed, setReviewed] = useState({});
    const [deciding, setDeciding] = useState(null);
    // The refuse dialog, held open on one induction while its reason is typed.
    const [rejecting, setRejecting] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    // Refusing an induction only says what is NOT acceptable. The useful next
    // step is asking the person to do it again, so the dialog offers it rather
    // than leaving the admin to remember Run Induction afterwards.
    const [resendAfterReject, setResendAfterReject] = useState(true);

    // Who we can reach, and how. Email first, falling back to SMS — the same
    // order the invite path uses.
    const inviteChannel =
      viewSummary && viewSummary.email
        ? "email"
        : viewSummary && viewSummary.phone
        ? "sms"
        : null;

    // A rejected induction is never reopened — it is the record of what was
    // refused and why (VER-D3). Asking again means a NEW induction on the same
    // form, with its own link.
    const startReplacement = (induction) =>
      visitorInductionService
        .start(viewSummary.id, induction.form_id)
        .then((res) => {
          if (!res || !res.success || !res.data || !res.data.induction) {
            message.warning(
              "Rejected, but a replacement induction could not be started. Use Run Induction when you're ready."
            );
            return null;
          }
          return visitorInductionService
            .invite(res.data.induction.id, inviteChannel)
            .then((sent) => {
              if (sent && sent.success) {
                message.success(`A new induction link has been sent by ${inviteChannel}`);
              } else {
                // The induction exists either way — it is on the record and can
                // be run or re-sent. Only the message failed.
                message.warning(
                  "Rejected and a new induction started, but the link could not be sent. Send it again from Run Induction."
                );
              }
              return res.data.induction;
            });
        })
        .catch(() => {
          message.warning(
            "Rejected, but a replacement induction could not be started. Use Run Induction when you're ready."
          );
          return null;
        });

    // Refresh after a decision so the drawer shows the new state without the
    // list underneath going stale.
    const reloadInductions = () =>
      visitorInductionService
        .getHistory(viewSummary.id, INDUCTION_HISTORY_LIMIT)
        .then((res) => {
          setInductions((res && res.success && res.data && res.data.inductions) || []);
          if (onVerified) onVerified();
        })
        .catch(() => {});

    // Owner/admin on the property, or the person who ran this one. The server
    // enforces the same rule — this only decides whether to show the controls.
    const mayDecide = (induction) =>
      !!canVerify ||
      (!!currentUserId && Number(induction.run_by_id) === Number(currentUserId));

    const decide = (induction, approve, reason) => {
      setDeciding(induction.id);
      const call = approve
        ? visitorInductionService.verify(induction.id)
        : visitorInductionService.reject(induction.id, reason);
      return call
        .then((res) => {
          if (!res || !res.success) {
            message.error((res && res.message) || "Could not record the decision");
            return false;
          }
          message.success(res.message || (approve ? "Induction verified" : "Induction rejected"));
          return reloadInductions().then(() => true);
        })
        .catch(() => {
          message.error("Could not record the decision");
          return false;
        })
        .finally(() => setDeciding(null));
    };

    const submitRejection = () => {
      const reason = rejectReason.trim();
      if (!reason) {
        message.error("Please say why this induction is being refused");
        return;
      }
      // Held before the dialog closes — `rejecting` is cleared below and the
      // replacement needs the form it was refused on.
      const induction = rejecting;
      decide(induction, false, reason).then((ok) => {
        if (!ok) {
          return;
        }
        setRejecting(null);
        setRejectReason("");
        if (resendAfterReject && inviteChannel && induction.form_id) {
          startReplacement(induction).then(reloadInductions);
        }
      });
    };

    // Open a completed induction's answers. The submission carries the schema
    // snapshot taken when it was submitted, so a record made against an older
    // version of the form still renders its own field set.
    const viewInductionAnswers = (induction) => {
      setOpeningSubmission(induction.id);
      visitorInductionService
        .getSubmission(induction.id)
        .then((res) => {
          if (!res || !res.success) {
            message.error((res && res.message) || "Could not load the induction answers");
            return;
          }
          const sub = res.data.submission;
          // Only once the answers are actually on screen does this count as
          // having been reviewed.
          setReviewed((seen) => ({ ...seen, [induction.id]: true }));
          setInductionResult({
            form: {
              name: induction.form_name,
              schema_json: sub.schema_json,
            },
            user: sub.submitter_name || (props.viewSummary && props.viewSummary.name) || "Visitor",
            agree_date: sub.submitted_at,
            global_form_submission: [
              {
                ...(sub.answers || {}),
                submisison_date: sub.submitted_at,
                _schema: sub.schema_json || null,
              },
            ],
          });
        })
        .catch(() => message.error("Could not load the induction answers"))
        .finally(() => setOpeningSubmission(null));
    };

    // Induction history is fetched per visitor rather than shipped with the
    // list payload — the list only carries the summary the badge needs.
    useEffect(() => {
      if (!props.visibleViewSummaryForm || !viewSummary || !viewSummary.id) return;
      let cancelled = false;
      setLoadingInductions(true);
      visitorInductionService
        .getHistory(viewSummary.id, INDUCTION_HISTORY_LIMIT)
        .then((res) => {
          if (cancelled) return;
          setInductions((res && res.success && res.data && res.data.inductions) || []);
        })
        .catch((e) => {
          if (!cancelled) {
            console.error("Failed to load induction history:", e);
            setInductions([]);
          }
        })
        .finally(() => {
          if (!cancelled) setLoadingInductions(false);
        });
      return () => { cancelled = true; };
    }, [props.visibleViewSummaryForm, viewSummary && viewSummary.id]);

const cancelModal = () => {
    props.setVisibleViewSummaryForm(false);
}



                
  return (

    <VisitorSummaryStyles
    className='visitor-modal'
    forceRender={true}
    open={props.visibleViewSummaryForm}
    title={<IntlMessages id="Visitor Details" />}
    onCancel={cancelModal}
    maskClosable={true}
    footer={[
      <Button key="back" onClick={cancelModal} className="cancel-button">
        {<IntlMessages id="propertyPage.modal.cancel" />}
      </Button>,
      
    ]}
  >
    <div style={{
        paddingLeft:'10px',
        width: "95%", height: "400px"
      }}>
        <br />
        
        <Timeline>
            
         
                <p>{moment(props.viewSummary.created_at).format('ll')}</p>
                {/* Title and type both come from the real visitor_type column via
                    getVisitorType (which also handles legacy rows that only carry
                    visit_purpose). The `user_type` field this used to read is a
                    hardcoded constant on VisitorInfo — it returns "Visitor" for
                    every row, so contractors were mislabelled here while the
                    Visitors list, which already used getVisitorType, was right. */}
                <Card title={summaryType} bordered={true}>
                <Row>
                    <Col xs={{ span: 9 }} md={{ span: 9 }}>
                      <p className="card_content">Visitor Type</p>
                    </Col>
                    <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                      <b className="card_value">{summaryType}</b>
                    </Col>
                  </Row>
                  {props.viewSummary.business_name && (
                    <Row>
                      <Col xs={{ span: 9 }} md={{ span: 9 }}>
                        <p className="card_content">Business Name</p>
                      </Col>
                      <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                        <b className="card_value">{props.viewSummary.business_name}</b>
                      </Col>
                    </Row>
                  )}
                <Row>
                    <Col xs={{ span: 9 }} md={{ span: 9 }}>
                      <p className="card_content">Visitor Email</p>
                    </Col>
                    <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                      <b className="card_value">{props.viewSummary.email ? props.viewSummary.email : '-'}</b>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={{ span: 9 }} md={{ span: 9 }}>
                      <p className="card_content">Emergency Contact Name</p>
                    </Col>
                    <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                      <b className="card_value">{props.viewSummary.emergency_name ? props.viewSummary.emergency_name : '-'}</b>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={{ span: 9 }} md={{ span: 9 }}>
                      <p className="card_content">Emergency Contact (relationship)</p>
                    </Col>
                    <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                      <b className="card_value">{props.viewSummary.emergency_contact ? props.viewSummary.emergency_contact : '-'}</b>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={{ span: 9 }} md={{ span: 9 }}>
                      <p className="card_content">Emergency Mobile Number</p>
                    </Col>
                    <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                      <b className="card_value">{props.viewSummary.emergency_mobile_number ? props.viewSummary.emergency_mobile_number :'-'}</b>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={{ span: 9 }} md={{ span: 9 }}>
                      <p className="card_content">Mobile Number</p>
                    </Col>
                    <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                      <b className="card_value">{props.viewSummary.phone ?props.viewSummary.phone : '-' }</b>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={{ span: 9 }} md={{ span: 9 }}>
                      <p className="card_content">Birthday</p>
                    </Col>
                    <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                      <b className="card_value">{fmtDay(props.viewSummary.birthday)}</b>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={{ span: 9 }} md={{ span: 9 }}>
                      <p className="card_content">Updated Date</p>
                    </Col>
                    <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                      <b className="card_value">{fmtDay(props.viewSummary.updated_at)}</b>
                    </Col>
                  </Row>
                </Card>

                <Card title="Inductions" bordered={true} style={{ marginTop: 16 }}>
                  {loadingInductions ? (
                    <p className="card_content">Loading…</p>
                  ) : inductions.length === 0 ? (
                    <p className="card_content">
                      No induction has been run for this visitor.
                    </p>
                  ) : (
                    inductions.map((induction) => {
                      const meta = INDUCTION_STATE_META[induction.state];
                      return (
                        <Row key={induction.id} style={{ marginBottom: 12 }}>
                          <Col xs={{ span: 9 }} md={{ span: 9 }}>
                            <p className="card_content">
                              {induction.form_name || getFormPurposeLabel(induction.purpose)}
                            </p>
                          </Col>
                          <Col xs={{ span: 13, offset: 2 }} md={{ span: 13, offset: 2 }}>
                            {/* Status and its detail on the left, the action on
                                the right edge — the standard list-row shape, so
                                the action reads as a control rather than as one
                                more line of the description. */}
                            <div className="induction-row">
                              <div className="induction-row-detail">
                                <b className="card_value">{meta ? meta.label : induction.status}</b>
                                {induction.completed_at && (
                                  <div className="card_content">
                                    {moment(induction.completed_at).format("lll")}
                                    {induction.completed_by === "visitor"
                                      ? " · completed by the visitor"
                                      : induction.run_by_name
                                      ? ` · run by ${induction.run_by_name}`
                                      : ""}
                                  </div>
                                )}
                                {induction.expires_at && (
                                  <div className="card_content">
                                    {induction.expiry_status === "expired"
                                      ? `Expired ${moment(induction.expires_at).format("ll")}`
                                      : `Expires ${moment(induction.expires_at).format("ll")}`}
                                  </div>
                                )}
                                {/* Nobody ran this one today — it was carried over
                                    from an earlier visit while still valid. Saying so
                                    keeps the history honest about what happened when. */}
                                {induction.carried_from_id && (
                                  <div className="card_content">
                                    Carried forward from an earlier visit
                                  </div>
                                )}
                                {/* Who accepted it, and whether that was a
                                    second pair of eyes. Self-verified is shown
                                    rather than hidden: it is allowed, and the
                                    record should not imply a review that did
                                    not happen. */}
                                {induction.verified_at && (
                                  <div className="card_content">
                                    {induction.verification_status === "rejected"
                                      ? "Rejected"
                                      : "Verified"}{" "}
                                    {moment(induction.verified_at).format("lll")}
                                    {induction.verified_by_name
                                      ? ` · by ${induction.verified_by_name}`
                                      : ""}
                                    {induction.self_verified ? " · self-verified" : ""}
                                  </div>
                                )}
                                {induction.rejection_reason && (
                                  <div className="card_content induction-rejected-reason">
                                    Reason: {induction.rejection_reason}
                                  </div>
                                )}
                              </div>
                              {/* Only a submitted induction has answers to show; a
                                  pending or link-sent one has nothing behind it. */}
                              <div className="induction-row-actions">
                                {induction.form_submission_id && (
                                  <AntTooltip title="View the answers recorded for this induction">
                                    <Button
                                      className="induction-view-btn"
                                      size="small"
                                      icon={<EyeOutlined />}
                                      aria-label="View induction answers"
                                      loading={openingSubmission === induction.id}
                                      onClick={() => viewInductionAnswers(induction)}
                                    >
                                      View Answers
                                    </Button>
                                  </AntTooltip>
                                )}
                                {/* Submitted and waiting on a person. The
                                    controls sit next to the answers, and stay
                                    disabled until those answers have been
                                    opened — accepting an induction means you
                                    read it. */}
                                {induction.awaiting_verification && mayDecide(induction) && (
                                  <>
                                    <AntTooltip
                                      title={
                                        reviewed[induction.id]
                                          ? "Accept this induction"
                                          : "Open the answers before accepting"
                                      }
                                    >
                                      <Button
                                        className="induction-verify-btn"
                                        size="small"
                                        type="primary"
                                        icon={<CheckOutlined />}
                                        disabled={!reviewed[induction.id]}
                                        loading={deciding === induction.id}
                                        onClick={() => decide(induction, true)}
                                      >
                                        Verify
                                      </Button>
                                    </AntTooltip>
                                    <AntTooltip
                                      title={
                                        reviewed[induction.id]
                                          ? "Refuse this induction"
                                          : "Open the answers before refusing"
                                      }
                                    >
                                      <Button
                                        className="induction-reject-btn"
                                        size="small"
                                        danger
                                        icon={<CloseOutlined />}
                                        disabled={!reviewed[induction.id]}
                                        onClick={() => {
                                          setRejectReason("");
                                          setResendAfterReject(true);
                                          setRejecting(induction);
                                        }}
                                      >
                                        Reject
                                      </Button>
                                    </AntTooltip>
                                  </>
                                )}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      );
                    })
                  )}
                </Card>

        </Timeline>

      </div>
      {/* Same viewer the Forms page uses for schema submissions, fed the same
          shape (see FormModal::showSchemaSubmission) so induction answers render
          exactly like every other submission. */}
      {/* Refusing needs a reason: the rejected record is the only trace that it
          happened, and without a reason nobody can act on it. */}
      <Modal
        visible={!!rejecting}
        title="Reject this induction"
        okText="Reject induction"
        okButtonProps={{ danger: true, loading: deciding === (rejecting && rejecting.id) }}
        onOk={submitRejection}
        onCancel={() => {
          setRejecting(null);
          setRejectReason("");
        }}
        destroyOnClose
      >
        <p className="card_content">
          {rejecting && (rejecting.form_name || "This induction")} will read as not
          inducted, and a new induction has to be run. This cannot be undone.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={500}
          showCount
          value={rejectReason}
          placeholder="Why is it being refused? The person and the next reviewer will see this."
          onChange={(e) => setRejectReason(e.target.value)}
        />
        {/* The whole point of refusing is usually to get it done properly, so
            asking again is offered here rather than left as a separate action
            to remember. */}
        <div style={{ marginTop: 12 }}>
          <Checkbox
            checked={resendAfterReject && !!inviteChannel}
            disabled={!inviteChannel}
            onChange={(e) => setResendAfterReject(e.target.checked)}
          >
            {inviteChannel
              ? `Start a new induction and send ${
                  (viewSummary && viewSummary.name) || "them"
                } a fresh link by ${inviteChannel}`
              : "No email or phone on this visitor — use Run Induction to do it again in person"}
          </Checkbox>
        </div>
      </Modal>
      {inductionResult && (
        <ViewSurveyResultModal
          visibleViewSurveyResult={!!inductionResult}
          cancelViewSurveyResult={() => setInductionResult(null)}
          surveyData={inductionResult}
          type="system_forms"
        />
      )}
      </VisitorSummaryStyles>
  );
};

export default VisitorViewSummary;
